import React, { useEffect, useMemo, useState } from "react";
import { Menu, Tooltip, Badge } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import "./SideBarDoctor.css";
import { logoutDoctorLocal } from "../../authDoctor/redux/slices/doctorsSlice";
import { markSessionLogout } from "../../auth/sessionLogout";
import lightLogo from "../../assets/images/medilink.svg";
import darkLogo from "../../assets/images/darkmedilink.svg";
import collapsedLogo from "../../assets/images/logomedilink.svg";

import useSidebarIndicators from "../../notifications/hooks/useSidebarIndicators";


/* ===== Icon wrapper ===== */
const IconWrap = ({ children, className = "" }) => (
  <span className={`sb-icon ${className}`} aria-hidden="true">
    {children}
  </span>
);

/* ===== Indicateurs ===== */
// Sidebar réduite : point sur l'icône
const IndicatorIcon = ({ icon, count, collapsed }) =>
  collapsed && count ? (
    <Badge dot offset={[-2, 4]}>
      {icon}
    </Badge>
  ) : (
    icon
  );

// Sidebar ouverte : compteur à côté du label
// Sidebar ouverte : compteur affiché à droite du label (dans le flux, sans superposition)
const IndicatorLabel = ({ children, count, collapsed, a11yLabel }) =>
  !collapsed && count ? (
    <span className="sb-label-with-badge">
      <span className="sb-label-with-badge__text">{children}</span>
      <Badge
        count={count}
        overflowCount={99}
        className="sb-inline-badge"
        title={a11yLabel ? `${count} ${a11yLabel}` : String(count)}
      />
    </span>
  ) : (
    <>{children}</>
  );

/* ==== ICONES ==== */
const IHome = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
    </svg>
  </IconWrap>
);

const IProfile = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
      <path d="M5 19.5a7 7 0 0 1 14 0" />
    </svg>
  </IconWrap>
);

const IPerscription = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </svg>
  </IconWrap>
);

const IBilling = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.2" />
      <path d="M2.5 9.5h19" />
      <path d="M6 14.5h4" />
    </svg>
  </IconWrap>
);

const IAvailability = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
      <circle cx="12" cy="15" r="2" />
    </svg>
  </IconWrap>
);

const IAppointments = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
      <path d="M9 14h6M9 17h3" />
    </svg>
  </IconWrap>
);

const IMessages = () => (
  <IconWrap>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path
        d="M2.992 16.342C3.139 16.713 3.172 17.119 3.086 17.509l-1.065 3.29a1 1 0 0 0 1.291 1.205l3.413-.998a2 2 0 0 1 1.099.092A10 10 0 1 0 2.992 16.342Z"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
      />
    </svg>
  </IconWrap>
);

const IPatients = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  </IconWrap>
);

const IPharmacy = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path
        d="M10.5 20.5L20.5 10.5c1.9-1.9 1.9-5 0-6.9s-5-1.9-6.9 0L3.5 13.5c-1.9 1.9-1.9 5 0 6.9s5 1.9 6.9 0Z"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
      />
      <path d="M8.5 8.5L15.5 15.5" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  </IconWrap>
);

const IClinics = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <rect x="4" y="11" width="4" height="9" rx="1" />
      <rect x="16" y="11" width="4" height="9" rx="1" />
      <rect x="8" y="7" width="8" height="13" rx="2" />
      <path d="M12 9v4M10 11h4" />
    </svg>
  </IconWrap>
);

const IAnalysisLab = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path
        d="M14 2V8c0 .34.08.67.25.96l5.51 10.08c.17.3.25.64.25.98a2 2 0 0 1-2 2H6a2 2 0 0 1-2-1.98c0-.34.08-.68.25-.98L9.76 8.96A2 2 0 0 0 10 8V2"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
      />
      <path d="M6.453 15H17.547" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  </IconWrap>
);

const INurses = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path d="M9 14H15M12 17V11M8 3H16V5H8V3Z" />
      <rect x="4" y="5" width="16" height="17" rx="2" />
    </svg>
  </IconWrap>
);

/* Icone Medihub (icone du dossier parent, comme "Places") */
const IMedicalQuestions = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  </IconWrap>
);

/* Icone dédiée pour MedicalQuestionsDoctor (page indépendante, distincte de Medihub) */
const IMedicalQuestionsDoctor = () => (
  <IconWrap>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
      <path d="M8.5 12h.01M12 12h.01M15.5 12h.01" strokeWidth="2.2" />
    </svg>
  </IconWrap>
);

/* ➕ Icones pour les enfants de Medihub */
const ICaseDiscussions = () => (
  <IconWrap>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
      <path d="M8.5 12h.01M12 12h.01M15.5 12h.01" strokeWidth="2.2" />
    </svg>
  </IconWrap>
);

const ICreateCase = () => (
  <IconWrap>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  </IconWrap>
);

const IDashboard = () => (
  <IconWrap>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  </IconWrap>
);

const IWaitingRoom = () => (
  <IconWrap>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="9" cy="10" r="2" />
      <circle cx="15" cy="10" r="2" />
      <path d="M6 17c0-2 1.5-3 3-3h6c1.5 0 3 1 3 3" />
    </svg>
  </IconWrap>
);

/* Le wrapper est déjà aria-hidden : plus besoin d'aria-label en dur ici. */
const IPlaces = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path d="M12 21s7-5.2 7-12a7 7 0 0 0-14 0c0 6.8 7 12 7 12Z" />
      <circle cx="12" cy="9" r="2.4" />
    </svg>
  </IconWrap>
);

const IAssistants = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <circle cx="9" cy="8" r="3" />
      <circle cx="16.5" cy="9.5" r="2.5" />
      <path d="M4.5 18a4.5 4.5 0 0 1 9 0" />
      <path d="M13.5 18a3.5 3.5 0 0 1 7 0" />
    </svg>
  </IconWrap>
);

/* ===== clés virtuelles des sous-menus (pas des routes) ===== */
const PLACES_MENU_KEY = "doctor-places-menu";
const MEDIHUB_MENU_KEY = "doctor-medihub-menu";

/* ===== mapping sélection -> clé ===== */
function getSelectedKey(pathname) {
  const p = String(pathname || "");

  if (p === "/feeddoctor" || p.startsWith("/feeddoctor/")) return "/feeddoctor";
  if (p === "/doctorprofile" || p.startsWith("/doctorprofile/")) return "/doctorprofile";
  if (p === "/cabinet-dashboard" || p.startsWith("/cabinet-dashboard/")) return "/cabinet-dashboard";
  if (p === "/subscription" || p.startsWith("/subscription/")) return "/subscription";
  if (p === "/availability" || p.startsWith("/availability/")) return "/availability";
  if (p === "/appointments" || p.startsWith("/appointments/")) return "/appointments";
  if (p === "/AppointmentsDoctor" || p.startsWith("/AppointmentsDoctor/")) return "/AppointmentsDoctor";
  if (p === "/waiting-room" || p.startsWith("/waiting-room/")) return "/waiting-room";
  if (p === "/assistants" || p.startsWith("/assistants/")) return "/assistants";
  if (
    p === "/patients" ||
    p.startsWith("/patients/") ||
    p === "/PatientRecord" ||
    p.startsWith("/doctor/patients/")
  ) return "/patients";
  if (p === "/Perscription" || p.startsWith("/Perscription/")) return "/Perscription";
  if (
    p === "/doctor-medicines" ||
    p.startsWith("/doctor-medicines/") ||
    p.startsWith("/Medications/")
  ) return "/doctor-medicines";
  /* Enfants de Medihub */
  if (p === "/case-discussions/create") return "/case-discussions/create";
  if (p === "/case-discussions" || p.startsWith("/case-discussions/")) return "/case-discussions";
  /* Medical Questions Doctor : item unique et indépendant */
  if (p === "/medical-questions-Doctor" || p.startsWith("/medical-questions-Doctor/")) return "/medical-questions-Doctor";
  if (p === "/messages" || p.startsWith("/messages/")) return "/messages";
  if (p === "/pharmacies" || p.startsWith("/pharmacies/")) return "/pharmacies";
  if (p === "/clinics" || p.startsWith("/clinics/")) return "/clinics";
  if (p === "/analysis-lab" || p.startsWith("/analysis-lab/")) return "/analysis-lab";
  if (p === "/nurses" || p.startsWith("/nurses/")) return "/nurses";

  return p;
}

function isPlaceRoute(pathname) {
  const p = String(pathname || "");
  return (
    p === "/pharmacies" || p.startsWith("/pharmacies/") ||
    p === "/clinics" || p.startsWith("/clinics/") ||
    p === "/analysis-lab" || p.startsWith("/analysis-lab/") ||
    p === "/nurses" || p.startsWith("/nurses/")
  );
}

/* ➕ le sous-menu Medihub doit rester ouvert si on est sur une de ses sous-routes */
function isMedihubRoute(pathname) {
  const p = String(pathname || "");
  return p === "/case-discussions" || p.startsWith("/case-discussions/");
}

const DOCTOR_INDICATOR_ROUTES = {
  messages: "/messages",
  appointments: "/AppointmentsDoctor",
  waitingRoom: "/waiting-room",
};

/* ===== SideBarDoctor ===== */
export default function SideBarDoctor({ collapsed = false, onMenuClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  // Direction du texte selon la langue active (ar => rtl)
  const isRtl = i18n.dir(i18n.language) === "rtl";

  const selectedKey = getSelectedKey(location.pathname);
  const [openKeys, setOpenKeys] = useState([]);

    const { counts } = useSidebarIndicators({
    role: "doctor",
    routes: DOCTOR_INDICATOR_ROUTES,
    // enableSocket: true, // à activer seulement si NavbarDoctor n'ouvre pas déjà le socket
  });

  useEffect(() => {
    if (collapsed) { setOpenKeys([]); return; }
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (isPlaceRoute(location.pathname)) next.add(PLACES_MENU_KEY);
      if (isMedihubRoute(location.pathname)) next.add(MEDIHUB_MENU_KEY);
      return Array.from(next);
    });
  }, [collapsed, location.pathname]);

  // En RTL la sidebar est à droite : le tooltip doit s'ouvrir vers la gauche.
  const withMaybeTooltip = (text, node) =>
    collapsed ? (
      <Tooltip placement={isRtl ? "left" : "right"} title={text}>{node}</Tooltip>
    ) : (
      node
    );

  const itemsTop = useMemo(
    () => [
      // ── MAIN ──────────────────────────────────────────
      {
        key: "/feeddoctor",
        icon: <IHome />,
        label: t("navigation.feed", { defaultValue: "Feed" }),
      },
      {
        key: "/doctorprofile",
        icon: <IProfile />,
        label: t("navigation.yourProfile", { defaultValue: "Your Profile" }),
      },
      {
        key: "/messages",
        icon: <IMessages />,
        label: t("navigation.messages", { defaultValue: "Messages" }),
         badge: counts.messages,
        badgeLabel: "messages non lus",
      },

      { type: "divider", className: "sb-menu-divider" },

      // ── CABINET / WORK ────────────────────────────────
      {
        key: "/cabinet-dashboard",
        icon: <IDashboard />,
        label: t("navigation.cabinetDashboard", { defaultValue: "Cabinet Dashboard" }),
      },
      {
        key: "/availability",
        icon: <IAvailability />,
        label: t("navigation.availability", { defaultValue: "Availability" }),
      },
      {
        key: "/AppointmentsDoctor",
        icon: <IAppointments />,
        label: t("navigation.appointments", { defaultValue: "Appointments" }),
        badge: counts.appointments,
        badgeLabel: "rendez-vous non lus",
      },
      {
        key: "/waiting-room",
        icon: <IWaitingRoom />,
        label: t("navigation.waitingRoom", { defaultValue: "Waiting Room" }),
        badge: counts.waitingRoom,
        badgeLabel: "patients en salle d'attente",
      },
      {
        key: "/assistants",
        icon: <IAssistants />,
        label: t("navigation.assistants", { defaultValue: "Assistants" }),
      },
      {
        key: "/patients",
        icon: <IPatients />,
        label: t("navigation.patients", { defaultValue: "Patients" }),
      },

      { type: "divider", className: "sb-menu-divider" },

      // ── MEDICAL TOOLS ─────────────────────────────────
      // ➕ Medihub : SOUS-MENU (comme Places), avec Case Discussions + Create Case
      {
        key: MEDIHUB_MENU_KEY,
        icon: <IMedicalQuestions />,
        label: t("navigation.medihub", { defaultValue: "Medihub" }),
        children: [
          {
            key: "/case-discussions",
            icon: <ICaseDiscussions />,
            label: t("navigation.caseDiscussions", { defaultValue: "Case Discussions" }),
          },
          {
            key: "/case-discussions/create",
            icon: <ICreateCase />,
            label: t("navigation.createCase", { defaultValue: "Create Case" }),
          },
        ],
      },
      // ➕ Medical Questions Doctor : item UNIQUE, séparé, sans sous-menu
      {
        key: "/medical-questions-Doctor",
        icon: <IMedicalQuestionsDoctor />,
        label: t("navigation.medicalQuestions", { defaultValue: "Medical Questions" }),
      },
      {
        key: "/Perscription",
        icon: <IPerscription />,
        label: t("navigation.prescription", { defaultValue: "Prescriptions" }),
      },

      { type: "divider", className: "sb-menu-divider" },

      // ── PLACES ────────────────────────────────────────
      {
        key: PLACES_MENU_KEY,
        icon: <IPlaces />,
        label: t("navigation.places", { defaultValue: "Places" }),
        children: [
          {
            key: "/pharmacies",
            icon: <IPharmacy />,
            label: t("navigation.pharmacy", { defaultValue: "Pharmacies" }),
          },
          {
            key: "/clinics",
            icon: <IClinics />,
            label: t("navigation.clinics", { defaultValue: "Clinics" }),
          },
          {
            key: "/analysis-lab",
            icon: <IAnalysisLab />,
            label: t("navigation.analysisLab", { defaultValue: "Analysis Labs" }),
          },
          {
            key: "/nurses",
            icon: <INurses />,
            label: t("navigation.nurses", { defaultValue: "Nurses" }),
          },
        ],
      },
      {
        key: "/subscription",
        icon: <IBilling />,
        label: t("navigation.subscription", { defaultValue: "Subscription" }),
      },
    ],
    // [t]
    [t, counts.messages, counts.appointments, counts.waitingRoom]
  );

  const buildMenuItem = (item) => {
    if (item.type === "divider") return item;

    // return {
    //   key: item.key,
    //   icon: item.icon,
    //   disabled: !!item.disabled,
    //   label: item.disabled ? (
    //     <span className="sb__item--disabled">{item.label}</span>
    //   ) : (
    //     withMaybeTooltip(
    //       typeof item.label === "string" ? item.label : "",
    //       <span className="sb__menu-label">{item.label}</span>
    //     )
    //   ),
    //   className: selectedKey === item.key ? "menu-item-selected" : "",
    //   children: item.children?.map(buildMenuItem),
    // };

        return {
      key: item.key,
      icon: (
        <IndicatorIcon
          icon={item.icon}
          count={item.badge}
          collapsed={collapsed}
        />
      ),
      disabled: !!item.disabled,
      label: item.disabled ? (
        <span className="sb__item--disabled">{item.label}</span>
      ) : (
        withMaybeTooltip(
          typeof item.label === "string" ? item.label : "",
          <span className="sb__menu-label">
            <IndicatorLabel
              count={item.badge}
              collapsed={collapsed}
              a11yLabel={item.badgeLabel}
            >
              {item.label}
            </IndicatorLabel>
          </span>
        )
      ),
      className: selectedKey === item.key ? "menu-item-selected" : "",
      children: item.children?.map(buildMenuItem),
    };
  };

  const flattenItems = (items) =>
    items.flatMap((item) => {
      if (item.type === "divider") return [];
      if (item.children) return [item, ...flattenItems(item.children)];
      return [item];
    });

  const handleMenuClick = ({ key }) => {
    const allItems = flattenItems(itemsTop);
    const item = allItems.find((i) => i.key === key);

    if (!item || item.disabled) return;
    // clés "virtuelles" des sous-menus : ne naviguent jamais
    if (key === PLACES_MENU_KEY || key === MEDIHUB_MENU_KEY) return;
    if (key === "/logout") {
      markSessionLogout("doctor");
      dispatch(logoutDoctorLocal());
      navigate("/LoginDoctor");
      return;
    }

    navigate(key);
    if (onMenuClick) onMenuClick();
  };

  return (
    <aside
      className={`sb ${collapsed ? "sb--collapsed" : ""}`}
      aria-label={t("navigation.sidebarLabel", { defaultValue: "Doctor navigation menu" })}
    >
      <div className="sb__header">
        {/* "Medilink" est un nom de marque : on ne le traduit pas. */}
        {collapsed ? (
          <img src={collapsedLogo} alt="Medilink" className="sb__logo sb__logo--collapsed" />
        ) : (
          <>
            <img src={lightLogo} alt="Medilink" className="sb__logo sb__logo--light" />
            <img src={darkLogo} alt="Medilink" className="sb__logo sb__logo--dark" />
          </>
        )}
      </div>

      <div className="sb__menu-group">
        <Menu
          className="sb__menu sb__menu--top"
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={[selectedKey]}
          openKeys={collapsed ? undefined : openKeys}
          onOpenChange={(keys) => setOpenKeys(keys)}
          onClick={handleMenuClick}
          items={itemsTop.map(buildMenuItem)}
        />
      </div>
    </aside>
  );
}